/**
 * Coulomb's law and the electric field of point charges, in SI units.
 *
 *   F = kₑ q₁ q₂ / (εᵣ r²)      force between two point charges (positive = repulsion)
 *   E = kₑ q / (εᵣ r²)          field of a point charge, pointing away from + and toward −
 *   V = kₑ q / (εᵣ r)           its electric potential
 *
 * kₑ = 1/(4π ε₀) = 8.987 551 7862 × 10⁹ N·m²/C² (CODATA 2022). Many charges superpose:
 * the field at a point is the vector sum of the fields of the charges, and the force on a
 * test charge q₀ there is q₀ E. Static point charges in a uniform medium of relative
 * permittivity εᵣ are assumed.
 */

import { COULOMB_CONSTANT } from "@/lib/tool-kit/physics-fields/constants";

export type CoulombError =
  | "missing"
  | "non-positive"
  | "zero-charge"
  | "zero-force"
  | "sign-mismatch"
  | "coincident"
  | "not-finite";

interface Failure {
  ok: false;
  error: CoulombError;
}

function finite(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

/* ── Force between two charges ────────────────────────────────────────────── */

export type CoulombTarget = "force" | "q1" | "q2" | "distance";

export interface CoulombInputs {
  /** Signed force, N: positive repels, negative attracts. */
  force: number | null;
  q1: number | null;
  q2: number | null;
  distance: number | null;
  /** Relative permittivity εᵣ of the medium (1 for vacuum). */
  permittivity: number | null;
}

export type Nature = "repulsive" | "attractive" | "none";

export interface CoulombSolution {
  force: number;
  q1: number;
  q2: number;
  distance: number;
  permittivity: number;
  nature: Nature;
}

const NEEDS: Record<CoulombTarget, readonly (keyof CoulombInputs)[]> = {
  force: ["q1", "q2", "distance", "permittivity"],
  q1: ["force", "q2", "distance", "permittivity"],
  q2: ["force", "q1", "distance", "permittivity"],
  distance: ["force", "q1", "q2", "permittivity"],
};

function natureOf(force: number): Nature {
  if (force === 0) {
    return "none";
  }
  return force > 0 ? "repulsive" : "attractive";
}

interface Values {
  force: number;
  q1: number;
  q2: number;
  distance: number;
}

/** Solve for a charge: q = F r² εᵣ / (kₑ q_other). */
function chargeFrom(
  force: number,
  other: number,
  distance: number,
  k: number
): number | Failure {
  if (other === 0) {
    return { ok: false, error: "zero-charge" };
  }
  return (force * distance ** 2) / (k * other);
}

/** The distance at which charges q₁ and q₂ exert the force F. */
function distanceFrom(v: Values, k: number): number | Failure {
  if (v.force === 0) {
    return { ok: false, error: "zero-force" };
  }
  if (v.q1 === 0 || v.q2 === 0) {
    return { ok: false, error: "zero-charge" };
  }
  const squared = (k * v.q1 * v.q2) / v.force;
  return squared <= 0
    ? { ok: false, error: "sign-mismatch" }
    : Math.sqrt(squared);
}

function compute(
  target: CoulombTarget,
  v: Values,
  k: number
): Values | Failure {
  if (target === "force") {
    return { ...v, force: (k * v.q1 * v.q2) / v.distance ** 2 };
  }
  if (target === "distance") {
    const distance = distanceFrom(v, k);
    return typeof distance === "number" ? { ...v, distance } : distance;
  }
  const charge = chargeFrom(
    v.force,
    target === "q1" ? v.q2 : v.q1,
    v.distance,
    k
  );
  if (typeof charge !== "number") {
    return charge;
  }
  return target === "q1" ? { ...v, q1: charge } : { ...v, q2: charge };
}

export function solveCoulomb(
  inputs: CoulombInputs,
  target: CoulombTarget
): { ok: true; value: CoulombSolution } | Failure {
  for (const key of NEEDS[target]) {
    if (!finite(inputs[key])) {
      return { ok: false, error: "missing" };
    }
  }
  const permittivity = inputs.permittivity ?? 1;
  const given: Values = {
    force: inputs.force ?? 0,
    q1: inputs.q1 ?? 0,
    q2: inputs.q2 ?? 0,
    distance: inputs.distance ?? 0,
  };
  if (permittivity <= 0 || (target !== "distance" && given.distance <= 0)) {
    return { ok: false, error: "non-positive" };
  }
  const solved = compute(target, given, COULOMB_CONSTANT / permittivity);
  if ("ok" in solved) {
    return solved;
  }
  const solution = { ...solved, permittivity };
  if (!Object.values(solution).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: { ...solution, nature: natureOf(solved.force) } };
}

/* ── Field of one point charge ────────────────────────────────────────────── */

export type FieldTarget = "field" | "charge" | "distance";

export interface PointFieldInputs {
  /** Field strength, N/C (a magnitude). */
  field: number | null;
  /** Charge, C. Signed unless the target is `charge`, when only its size is found. */
  charge: number | null;
  distance: number | null;
  permittivity: number | null;
  /** Used only when solving for the charge, to give the magnitude its sign. */
  negative: boolean;
}

export interface PointFieldSolution {
  /** Field magnitude, N/C. */
  field: number;
  /** Signed charge, C. */
  charge: number;
  distance: number;
  permittivity: number;
  /** Radial field component: positive points away from the charge. */
  radial: number;
  /** Potential kₑ q / (εᵣ r), V. */
  potential: number;
}

const FIELD_NEEDS: Record<FieldTarget, readonly (keyof PointFieldInputs)[]> = {
  field: ["charge", "distance", "permittivity"],
  charge: ["field", "distance", "permittivity"],
  distance: ["field", "charge", "permittivity"],
};

export function solvePointField(
  inputs: PointFieldInputs,
  target: FieldTarget
): { ok: true; value: PointFieldSolution } | Failure {
  for (const key of FIELD_NEEDS[target]) {
    if (!finite(inputs[key] as number | null)) {
      return { ok: false, error: "missing" };
    }
  }
  const permittivity = inputs.permittivity ?? 1;
  const k = COULOMB_CONSTANT / permittivity;
  let charge = inputs.charge ?? 0;
  let distance = inputs.distance ?? 0;
  let field = inputs.field ?? 0;
  if (permittivity <= 0 || (target !== "distance" && distance <= 0)) {
    return { ok: false, error: "non-positive" };
  }
  if (target !== "field" && field <= 0) {
    return { ok: false, error: "non-positive" };
  }
  if (target === "field") {
    field = (k * Math.abs(charge)) / distance ** 2;
  } else if (target === "charge") {
    const size = (field * distance ** 2) / k;
    charge = inputs.negative ? -size : size;
  } else {
    if (charge === 0) {
      return { ok: false, error: "zero-charge" };
    }
    distance = Math.sqrt((k * Math.abs(charge)) / field);
  }
  const solution = {
    field,
    charge,
    distance,
    permittivity,
    radial: Math.sign(charge) * field,
    potential: (k * charge) / distance,
  };
  if (!Object.values(solution).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/* ── Several charges ──────────────────────────────────────────────────────── */

export interface PointCharge {
  q: number;
  x: number;
  y: number;
}

export interface Contribution {
  charge: PointCharge;
  /** Distance from this charge to the field point, m. */
  distance: number;
  /** This charge's field at the point, N/C. */
  ex: number;
  ey: number;
  magnitude: number;
}

export interface SuperpositionSolution {
  ex: number;
  ey: number;
  magnitude: number;
  /** Direction of the field, degrees counter-clockwise from +x (−180…180]. */
  angle: number;
  /** Force on the test charge q₀ E — null when no test charge was given. */
  force: { x: number; y: number; magnitude: number; angle: number } | null;
  /** Total potential Σ kₑ q / (εᵣ r), V. */
  potential: number;
  contributions: Contribution[];
}

export function solveSuperposition(
  charges: readonly PointCharge[],
  point: { x: number; y: number },
  testCharge: number | null,
  permittivity: number
): { ok: true; value: SuperpositionSolution } | Failure {
  if (charges.length === 0) {
    return { ok: false, error: "missing" };
  }
  if (!(permittivity > 0)) {
    return { ok: false, error: "non-positive" };
  }
  const k = COULOMB_CONSTANT / permittivity;
  const contributions: Contribution[] = [];
  let ex = 0;
  let ey = 0;
  let potential = 0;
  for (const charge of charges) {
    const dx = point.x - charge.x;
    const dy = point.y - charge.y;
    const distance = Math.hypot(dx, dy);
    if (distance === 0) {
      return { ok: false, error: "coincident" };
    }
    const scale = (k * charge.q) / distance ** 3;
    const cx = scale * dx;
    const cy = scale * dy;
    ex += cx;
    ey += cy;
    potential += (k * charge.q) / distance;
    contributions.push({
      charge,
      distance,
      ex: cx,
      ey: cy,
      magnitude: Math.hypot(cx, cy),
    });
  }
  const force =
    testCharge === null
      ? null
      : {
          x: testCharge * ex,
          y: testCharge * ey,
          magnitude: Math.abs(testCharge) * Math.hypot(ex, ey),
          angle: directionOf(testCharge * ex, testCharge * ey),
        };
  const solution: SuperpositionSolution = {
    ex,
    ey,
    magnitude: Math.hypot(ex, ey),
    angle: directionOf(ex, ey),
    force,
    potential,
    contributions,
  };
  const numbers = [solution.ex, solution.ey, solution.potential];
  if (!numbers.every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/** Degrees counter-clockwise from +x; zero for a zero vector. */
export function directionOf(x: number, y: number): number {
  if (x === 0 && y === 0) {
    return 0;
  }
  return (Math.atan2(y, x) * 180) / Math.PI;
}
